/**
 * Normalized Uint32/Int32 vertex attributes map to unorm32x4/snorm32x4 in Three.js WebGPU,
 * which are not valid GPUVertexFormat values. Interleaved attributes were skipped by early
 * sanitization attempts because they are not BufferAttribute instances.
 *
 * Deinterleave affected geometry, then convert those normalized 32-bit integer attributes to
 * float (same interpretation WebGL uses). Safe for WebGL and WebGPU.
 */

import * as THREE from 'three';
import { deinterleaveAttribute as deinterleaveAttributeImpl } from 'three/addons/utils/BufferGeometryUtils.js';

import type { GLTF } from 'three/addons/loaders/GLTFLoader.js';

const deinterleaveAttribute = deinterleaveAttributeImpl as unknown as (
  attribute: THREE.InterleavedBufferAttribute
) => THREE.BufferAttribute;

function isInterleavedBufferAttribute(
  attr: THREE.BufferAttribute | THREE.InterleavedBufferAttribute | undefined
): attr is THREE.InterleavedBufferAttribute {
  return attr !== undefined && 'isInterleavedBufferAttribute' in attr && attr.isInterleavedBufferAttribute === true;
}

function deinterleaveGeometryAttributes(geometry: THREE.BufferGeometry): void {
  const attrMap = new Map<THREE.InterleavedBufferAttribute, THREE.BufferAttribute>();
  const attributes = geometry.attributes;

  for (const key of Object.keys(attributes)) {
    const attr = attributes[key as keyof typeof attributes];
    if (!isInterleavedBufferAttribute(attr)) {
      continue;
    }
    const interleaved = attr;
    if (!attrMap.has(interleaved)) {
      attrMap.set(interleaved, deinterleaveAttribute(interleaved));
    }
    (attributes as Record<string, THREE.BufferAttribute | THREE.InterleavedBufferAttribute>)[key] = attrMap.get(
      interleaved
    )!;
  }

  const morph = geometry.morphAttributes as Record<
    string,
    Array<THREE.BufferAttribute | THREE.InterleavedBufferAttribute> | undefined
  >;
  for (const morphName of Object.keys(morph)) {
    const morphArray = morph[morphName];
    if (!Array.isArray(morphArray)) {
      continue;
    }
    for (let i = 0; i < morphArray.length; i++) {
      const attr = morphArray[i];
      if (!isInterleavedBufferAttribute(attr)) {
        continue;
      }
      const interleaved = attr;
      if (!attrMap.has(interleaved)) {
        attrMap.set(interleaved, deinterleaveAttribute(interleaved));
      }
      morphArray[i] = attrMap.get(interleaved)!;
    }
  }
}

function convertNormalizedInt32Uint32ToFloat(
  attribute: THREE.BufferAttribute
): THREE.BufferAttribute | null {
  if (!attribute.normalized) {
    return null;
  }
  const source = attribute.array;
  if (!(source instanceof Uint32Array) && !(source instanceof Int32Array)) {
    return null;
  }
  const converted = new Float32Array(source.length);
  if (source instanceof Uint32Array) {
    const max = 0xFFFFFFFF;
    for (let i = 0; i < source.length; i++) {
      converted[i] = source[i]! / max;
    }
  } else {
    const max = 2147483647;
    for (let i = 0; i < source.length; i++) {
      converted[i] = Math.max(-1, source[i]! / max);
    }
  }
  return new THREE.Float32BufferAttribute(converted, attribute.itemSize);
}

function sanitizeBufferGeometryForGpu(geometry: THREE.BufferGeometry): void {
  deinterleaveGeometryAttributes(geometry);

  for (const name of Object.keys(geometry.attributes)) {
    const attribute = geometry.attributes[name];
    if (!(attribute instanceof THREE.BufferAttribute)) {
      continue;
    }
    const converted = convertNormalizedInt32Uint32ToFloat(attribute);
    if (converted) {
      attribute.dispose();
      geometry.setAttribute(name, converted);
    }
  }
}

/**
 * Run after GLTFLoader.parse: fixes geometry for WebGPU (and keeps behavior consistent on WebGL).
 * Uses a geometry WeakSet so shared BufferGeometry is only processed once.
 */
export function sanitizeGltfLoadedModel(gltf: GLTF): void {
  const seen = new WeakSet<THREE.BufferGeometry>();
  const visitGeometry = (geometry: THREE.BufferGeometry): void => {
    if (seen.has(geometry)) {
      return;
    }
    seen.add(geometry);
    sanitizeBufferGeometryForGpu(geometry);
  };

  const roots = gltf.scenes?.length ? gltf.scenes : [gltf.scene];
  for (const root of roots) {
    if (!root) {
      continue;
    }
    root.traverse((obj) => {
      if (obj instanceof THREE.Mesh && obj.geometry) {
        visitGeometry(obj.geometry);
      }
    });
  }
}
